import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Typography,
  Button,
  Grid,
  Paper,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Divider,
} from "@mui/material";
import { useTheme } from "../../theming";
import { useResponsive } from "../../contexts/ResponsiveContext";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

const CompetitorAnalysisSection = ({ data, isEditing, onSave }) => {
  const theme = useTheme();
  const { isMobile, isTablet, isDesktop } = useResponsive();
  const [editedData, setEditedData] = useState(
    data || {
      competitors: [],
      consolidatedRecommendations: [],
    }
  );

  useEffect(() => {
    if (data) {
      setEditedData(data);
    }
  }, [data]);

  const handleChange = (field, value) => {
    setEditedData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCompetitorChange = (index, field, value) => {
    const newCompetitors = [...editedData.competitors];
    newCompetitors[index] = {
      ...newCompetitors[index],
      [field]: value,
    };
    handleChange("competitors", newCompetitors);
  };

  const handleRecommendationChange = (index, value) => {
    const newRecommendations = [...editedData.consolidatedRecommendations];
    newRecommendations[index] = value;
    handleChange("consolidatedRecommendations", newRecommendations);
  };

  const addCompetitor = () => {
    handleChange("competitors", [
      ...editedData.competitors,
      {
        name: "",
        analysis: "",
        recommendations: [],
      },
    ]);
  };

  const removeCompetitor = (index) => {
    const newCompetitors = editedData.competitors.filter((_, i) => i !== index);
    handleChange("competitors", newCompetitors);
  };

  const addRecommendation = () => {
    handleChange("consolidatedRecommendations", [
      ...editedData.consolidatedRecommendations,
      "",
    ]);
  };

  const removeRecommendation = (index) => {
    const newRecommendations = editedData.consolidatedRecommendations.filter(
      (_, i) => i !== index
    );
    handleChange("consolidatedRecommendations", newRecommendations);
  };

  const handleCompetitorRecommendationChange = (
    competitorIndex,
    recIndex,
    value
  ) => {
    const newCompetitors = [...editedData.competitors];
    const recommendations = [
      ...newCompetitors[competitorIndex].recommendations,
    ];
    recommendations[recIndex] = value;
    newCompetitors[competitorIndex] = {
      ...newCompetitors[competitorIndex],
      recommendations,
    };
    handleChange("competitors", newCompetitors);
  };

  const addCompetitorRecommendation = (competitorIndex) => {
    const newCompetitors = [...editedData.competitors];
    newCompetitors[competitorIndex] = {
      ...newCompetitors[competitorIndex],
      recommendations: [...newCompetitors[competitorIndex].recommendations, ""],
    };
    handleChange("competitors", newCompetitors);
  };

  const removeCompetitorRecommendation = (competitorIndex, recIndex) => {
    const newCompetitors = [...editedData.competitors];
    const recommendations = newCompetitors[
      competitorIndex
    ].recommendations.filter((_, i) => i !== recIndex);
    newCompetitors[competitorIndex] = {
      ...newCompetitors[competitorIndex],
      recommendations,
    };
    handleChange("competitors", newCompetitors);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(editedData);
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Grid container spacing={3}>
        {/* <Grid item xs={12}>
          <Typography
            variant="h6"
            sx={{
              color: theme.primaryColor,
              mb: 2,
            }}
          >
            Competitor Analysis
          </Typography>
        </Grid> */}

        <Grid item xs={12}>
          <Typography
            variant="subtitle1"
            sx={{
              color: theme.textColor,
              mb: 2,
            }}
          >
            Competitors
          </Typography>

          {isEditing ? (
            <Box>
              {editedData.competitors.map((competitor, index) => (
                <Accordion
                  key={index}
                  sx={{
                    backgroundColor: theme.backgroundColor,
                    color: theme.textColor,
                    mb: 2,
                    "&:before": {
                      display: "none",
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <ExpandMoreIcon sx={{ color: theme.textColor }} />
                    }
                    sx={{
                      borderBottom: `1px solid ${theme.primaryColor}40`,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <TextField
                        fullWidth
                        label="Competitor Name"
                        value={competitor.name}
                        onChange={(e) =>
                          handleCompetitorChange(index, "name", e.target.value)
                        }
                        sx={{
                          mr: 2,
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: `${theme.primaryColor}40`,
                            },
                            "&:hover fieldset": {
                              borderColor: theme.primaryColor,
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: theme.primaryColor,
                            },
                          },
                          "& .MuiInputLabel-root": {
                            color: theme.textColor,
                          },
                          "& .MuiInputBase-input": {
                            color: theme.textColor,
                          },
                        }}
                      />
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          removeCompetitor(index);
                        }}
                        sx={{
                          color: theme.accentColor,
                          "&:hover": {
                            backgroundColor: `${theme.accentColor}20`,
                          },
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          rows={4}
                          label="Analysis"
                          value={competitor.analysis}
                          onChange={(e) =>
                            handleCompetitorChange(
                              index,
                              "analysis",
                              e.target.value
                            )
                          }
                          sx={{
                            mb: 2,
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: `${theme.primaryColor}40`,
                              },
                              "&:hover fieldset": {
                                borderColor: theme.primaryColor,
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: theme.primaryColor,
                              },
                            },
                            "& .MuiInputLabel-root": {
                              color: theme.textColor,
                            },
                            "& .MuiInputBase-input": {
                              color: theme.textColor,
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            color: theme.textColor,
                            mb: 1,
                          }}
                        >
                          Recommendations
                        </Typography>
                        {competitor.recommendations.map((rec, recIndex) => (
                          <Box
                            key={recIndex}
                            sx={{
                              display: "flex",
                              gap: 1,
                              mb: 1,
                            }}
                          >
                            <TextField
                              fullWidth
                              value={rec}
                              onChange={(e) =>
                                handleCompetitorRecommendationChange(
                                  index,
                                  recIndex,
                                  e.target.value
                                )
                              }
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  "& fieldset": {
                                    borderColor: `${theme.primaryColor}40`,
                                  },
                                  "&:hover fieldset": {
                                    borderColor: theme.primaryColor,
                                  },
                                  "&.Mui-focused fieldset": {
                                    borderColor: theme.primaryColor,
                                  },
                                },
                                "& .MuiInputLabel-root": {
                                  color: theme.textColor,
                                },
                                "& .MuiInputBase-input": {
                                  color: theme.textColor,
                                },
                              }}
                            />
                            <IconButton
                              onClick={() =>
                                removeCompetitorRecommendation(index, recIndex)
                              }
                              sx={{
                                color: theme.accentColor,
                                "&:hover": {
                                  backgroundColor: `${theme.accentColor}20`,
                                },
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        ))}
                        <Button
                          startIcon={<AddIcon />}
                          onClick={() => addCompetitorRecommendation(index)}
                          sx={{
                            color: theme.primaryColor,
                            "&:hover": {
                              backgroundColor: `${theme.primaryColor}20`,
                            },
                          }}
                        >
                          Add Recommendation
                        </Button>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              ))}
              <Button
                startIcon={<AddIcon />}
                onClick={addCompetitor}
                sx={{
                  color: theme.primaryColor,
                  "&:hover": {
                    backgroundColor: `${theme.primaryColor}20`,
                  },
                }}
              >
                Add Competitor
              </Button>
            </Box>
          ) : (
            <Box>
              {editedData.competitors.map((competitor, index) => (
                <Accordion
                  key={index}
                  sx={{
                    backgroundColor: theme.backgroundColor,
                    color: theme.textColor,
                    mb: 2,
                    "&:before": {
                      display: "none",
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <ExpandMoreIcon sx={{ color: theme.textColor }} />
                    }
                    sx={{
                      borderBottom: `1px solid ${theme.primaryColor}40`,
                    }}
                  >
                    <Typography sx={{ fontWeight: "bold" }}>
                      {competitor.name}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            color: theme.textColor,
                            mb: 1,
                          }}
                        >
                          Analysis
                        </Typography>
                        <Typography>{competitor.analysis}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            color: theme.textColor,
                            mb: 1,
                          }}
                        >
                          Recommendations
                        </Typography>
                        <List>
                          {competitor.recommendations.map((rec, recIndex) => (
                            <ListItem key={recIndex}>
                              <ListItemText primary={rec} />
                            </ListItem>
                          ))}
                        </List>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
        </Grid>

        <Grid item xs={12}>
          <Typography
            variant="subtitle1"
            sx={{
              color: theme.textColor,
              mb: 2,
            }}
          >
            Consolidated Recommendations
          </Typography>

          {isEditing ? (
            <Box>
              {editedData.consolidatedRecommendations.map(
                (recommendation, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <TextField
                      fullWidth
                      value={recommendation}
                      onChange={(e) =>
                        handleRecommendationChange(index, e.target.value)
                      }
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: `${theme.primaryColor}40`,
                          },
                          "&:hover fieldset": {
                            borderColor: theme.primaryColor,
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: theme.primaryColor,
                          },
                        },
                        "& .MuiInputLabel-root": {
                          color: theme.textColor,
                        },
                        "& .MuiInputBase-input": {
                          color: theme.textColor,
                        },
                      }}
                    />
                    <IconButton
                      onClick={() => removeRecommendation(index)}
                      sx={{
                        color: theme.accentColor,
                        "&:hover": {
                          backgroundColor: `${theme.accentColor}20`,
                        },
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                )
              )}
              <Button
                startIcon={<AddIcon />}
                onClick={addRecommendation}
                sx={{
                  color: theme.primaryColor,
                  "&:hover": {
                    backgroundColor: `${theme.primaryColor}20`,
                  },
                }}
              >
                Add Recommendation
              </Button>
            </Box>
          ) : (
            <List>
              {editedData.consolidatedRecommendations.map(
                (recommendation, index) => (
                  <ListItem key={index}>
                    <ListItemText primary={recommendation} />
                  </ListItem>
                )
              )}
            </List>
          )}
        </Grid>

        {isEditing && (
          <Grid item xs={12}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                sx={{
                  backgroundColor: theme.primaryColor,
                  color: theme.buttonTextColor,
                  "&:hover": {
                    backgroundColor: `${theme.primaryColor}CC`,
                  },
                }}
              >
                Save Changes
              </Button>
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default CompetitorAnalysisSection;
